import type { EstimateAssumption, EstimateExclusion, EstimateWarning, EstimatorInput } from "../../types/index.js";
import {
  applyItem,
  findItem,
  type AppliedLineItem,
  type ProjectCalculationOutput,
  type ProjectCalculatorFn
} from "./shared.js";

/**
 * The Exterior Estimator (Phase 13B). `exterior` had no calculation
 * methodology at all before this phase.
 *
 * Per the brief's explicit instruction - "do not assume every exterior
 * project is a complete exterior renovation" and "allow users to select
 * relevant scope" - this is the one Phase 13B project type that does NOT
 * follow the anchor-line-item-plus-modifiers shape every other project
 * type (kitchen, basement, deck/patio, addition, garage, attic, all ten
 * interior rooms) uses. There is deliberately no single
 * `exterior_base_renovation` anchor and NO scope multiplier applied
 * anywhere in this file:
 *
 *   - An anchor + scope-multiplier design implicitly says "this project
 *     touches the whole exterior, to a degree controlled by one scope
 *     answer." That's exactly the assumption the brief says not to make -
 *     a homeowner replacing only their siding, or only re-painting trim,
 *     is not a smaller version of a "full exterior renovation"; they're
 *     doing a materially different, independently-scoped project.
 *   - Instead, exterior work is modeled as eight independent scope
 *     categories (siding, windows, entry doors, roofing-as-part-of-
 *     exterior-work, trim/fascia/soffit, exterior finishes/paint,
 *     structural repairs, design/compliance), each its own tiered
 *     single_select question defaulting to "none" (see
 *     packages/config/src/questions/exterior.ts) - the same "independent
 *     boolean/tiered gate, not one multi-select checklist question" shape
 *     the common questions already use for electrical/plumbing/
 *     demolition. An unanswered or "none" category prices nothing and
 *     contributes nothing to the total - never a default guess at "some"
 *     scope. The generic `scope`/`finish_level` common questions are
 *     still asked (finish level still adjusts each selected category's
 *     line item, same as everywhere else), but scope's usual "how much of
 *     the anchor is being redone" role has no anchor to apply to here -
 *     it's a deliberate no-op for `exterior`, not an oversight.
 *
 * Roofing scope decision: this is a RENOVATION estimator, not a
 * dedicated re-roofing estimator. `exterior_roofing_work_scope` is
 * therefore deliberately scoped to roofing work performed AS PART OF an
 * exterior renovation (minor repair, or flashing/edge work tied into a
 * siding job) - not a full roof tear-off/replacement, which is a
 * distinct, much larger project with its own material/labour profile
 * (shingles, underlayment, decking, disposal) that belongs in a
 * dedicated re-roofing estimator in a future phase. See "Known
 * compromises" in docs/exterior-estimator.md.
 */

const SIDING_ITEM_BY_TIER: Record<string, string | null> = {
  none: null,
  partial_repair: "exterior_siding_partial_repair",
  full_replacement: "exterior_siding_full_replacement"
};

const WINDOWS_ITEM_BY_TIER: Record<string, string | null> = {
  none: null,
  some_replaced: "exterior_windows_some_replaced",
  full_replacement: "exterior_windows_full_replacement"
};

const ROOFING_ITEM_BY_TIER: Record<string, string | null> = {
  none: null,
  minor_repair_flashing: "exterior_roofing_minor_repair",
  tied_to_siding_work: "exterior_roofing_tied_to_siding"
};

const TRIM_ITEM_BY_TIER: Record<string, string | null> = {
  none: null,
  basic: "exterior_trim_fascia_soffit_basic",
  premium: "exterior_trim_fascia_soffit_premium"
};

const PAINT_ITEM_BY_TIER: Record<string, string | null> = {
  none: null,
  spot_touch_up: "exterior_finishes_paint_spot",
  full_repaint: "exterior_finishes_paint_full"
};

const DESIGN_COMPLIANCE_ITEM_BY_TIER: Record<string, string | null> = {
  none: null,
  design_services: "exterior_design_services",
  heritage_hoa_compliance: "exterior_heritage_hoa_compliance"
};

function rawString(input: EstimatorInput, key: string): string | undefined {
  const value = input.rawAnswers[key];
  return typeof value === "string" ? value : undefined;
}

function sumTypical(applied: AppliedLineItem[]): number {
  return applied.reduce((total, a) => total + (a.result.typical?.amount ?? 0), 0);
}

export const calculateExterior: ProjectCalculatorFn = (input, catalog, regionalMultiplier) => {
  const applied: AppliedLineItem[] = [];
  const assumptions: EstimateAssumption[] = [];
  const exclusions: EstimateExclusion[] = [];
  const warnings: EstimateWarning[] = [];

  const finishLevel = input.finishLevel;
  const sqft = input.roomSize?.value;
  const hasSize = !!sqft && sqft > 0;
  let sizeDependentCategoryRequestedWithoutSize = false;

  // --- Siding (tiered, sqft) ---------------------------------------------------
  const sidingTier = rawString(input, "exterior_siding_scope") ?? "none";
  const sidingItemId = SIDING_ITEM_BY_TIER[sidingTier];
  if (sidingItemId) {
    if (hasSize) {
      const siding = applyItem(catalog, sidingItemId, { quantity: sqft, finishLevel, regionalMultiplier });
      if (siding) applied.push(siding);
    } else {
      sizeDependentCategoryRequestedWithoutSize = true;
      exclusions.push({ code: "excludes_exterior_siding_size_missing", message: "Siding work was selected but no exterior square footage was provided - excluded from this estimate." });
    }
  } else {
    exclusions.push({ code: "excludes_exterior_siding", message: "No siding work was requested - excluded from this estimate." });
  }

  // --- Windows (tiered, flat allowance - exact window count isn't collected) ---
  const windowsTier = rawString(input, "exterior_windows_scope") ?? "none";
  const windowsItemId = WINDOWS_ITEM_BY_TIER[windowsTier];
  if (windowsItemId) {
    const windows = applyItem(catalog, windowsItemId, { finishLevel, regionalMultiplier });
    if (windows) applied.push(windows);
    assumptions.push({
      code: "exterior_windows_flat_allowance",
      message: "Exterior window replacement is priced as a flat planning-level allowance for the selected tier (some vs. all windows), since an exact window count isn't collected - get an itemized quote once specific windows are identified."
    });
  } else {
    exclusions.push({ code: "excludes_exterior_windows", message: "No exterior window replacement was requested - excluded from this estimate." });
  }

  // --- Entry doors (tiered by count) -------------------------------------------
  const doorsTier = rawString(input, "exterior_doors_scope") ?? "none";
  const doorsQuantity = doorsTier === "one_door" ? 1 : doorsTier === "multiple_doors" ? 2 : 0;
  if (doorsQuantity > 0) {
    const doors = applyItem(catalog, "exterior_entry_door", { quantity: doorsQuantity, finishLevel, regionalMultiplier });
    if (doors) applied.push(doors);
    if (doorsTier === "multiple_doors") {
      assumptions.push({ code: "exterior_doors_quantity_assumed", message: "\"Multiple exterior doors\" was priced as exactly two doors for planning purposes - adjust once the exact count is known." });
    }
  } else {
    exclusions.push({ code: "excludes_exterior_doors", message: "No exterior entry door replacement was requested - excluded from this estimate." });
  }

  // --- Roofing work performed as part of this exterior project (NOT a full re-roof) ---
  const roofingTier = rawString(input, "exterior_roofing_work_scope") ?? "none";
  const roofingItemId = ROOFING_ITEM_BY_TIER[roofingTier];
  if (roofingItemId) {
    const roofing = applyItem(catalog, roofingItemId, { finishLevel, regionalMultiplier });
    if (roofing) applied.push(roofing);
    warnings.push({
      code: "exterior_roofing_scope_is_limited",
      message:
        "This estimate prices roofing work performed as part of this exterior renovation (minor repair or flashing/edge work tied into siding work), NOT a full roof tear-off/replacement. If a full re-roof is actually needed, get a dedicated roofing quote - this figure will significantly understate that cost.",
      severity: "caution"
    });
  } else {
    exclusions.push({ code: "excludes_exterior_roofing", message: "No roofing work was requested as part of this exterior project - excluded from this estimate. (Note: a full roof replacement is out of scope for this estimator entirely - see the roofing warning shown when roofing work is selected.)" });
  }

  // --- Trim / fascia / soffit (tiered, sqft proxy off exterior footprint) ------
  const trimTier = rawString(input, "exterior_trim_fascia_soffit_scope") ?? "none";
  const trimItemId = TRIM_ITEM_BY_TIER[trimTier];
  if (trimItemId) {
    if (hasSize) {
      const trim = applyItem(catalog, trimItemId, { quantity: sqft, finishLevel, regionalMultiplier });
      if (trim) applied.push(trim);
      assumptions.push({
        code: "exterior_trim_area_proxy",
        message: "Trim/fascia/soffit cost is priced per square foot of the provided exterior footprint, as a planning-level proxy for actual linear coverage, not a direct measurement."
      });
    } else {
      sizeDependentCategoryRequestedWithoutSize = true;
      exclusions.push({ code: "excludes_exterior_trim_size_missing", message: "Trim/fascia/soffit work was selected but no exterior square footage was provided - excluded from this estimate." });
    }
  } else {
    exclusions.push({ code: "excludes_exterior_trim", message: "No trim/fascia/soffit work was requested - excluded from this estimate." });
  }

  // --- Exterior finishes / paint (tiered, sqft) ---------------------------------
  const paintTier = rawString(input, "exterior_finishes_paint_scope") ?? "none";
  const paintItemId = PAINT_ITEM_BY_TIER[paintTier];
  if (paintItemId) {
    if (hasSize) {
      const paint = applyItem(catalog, paintItemId, { quantity: sqft, finishLevel, regionalMultiplier });
      if (paint) applied.push(paint);
    } else {
      sizeDependentCategoryRequestedWithoutSize = true;
      exclusions.push({ code: "excludes_exterior_paint_size_missing", message: "Exterior painting/refinishing was selected but no exterior square footage was provided - excluded from this estimate." });
    }
  } else {
    exclusions.push({ code: "excludes_exterior_paint", message: "No exterior finish/paint work was requested - excluded from this estimate." });
  }

  // --- Structural repairs (boolean, flat allowance - highly variable) ------------
  if (input.rawAnswers["exterior_structural_repairs"] === true) {
    const structural = applyItem(catalog, "exterior_structural_repairs", { finishLevel, regionalMultiplier });
    if (structural) applied.push(structural);
    warnings.push({
      code: "exterior_structural_repairs_highly_variable",
      message:
        "Exterior structural repairs (e.g. rot, settling, water damage to sheathing/framing) vary enormously in scope and can't be meaningfully estimated from a questionnaire - this is a wide planning-level allowance only. A contractor must open up the affected area to scope this accurately; actual cost could be well above or below this figure.",
      severity: "caution"
    });
  } else {
    exclusions.push({ code: "excludes_exterior_structural_repairs", message: "No structural repairs were indicated - excluded from this estimate." });
  }

  // --- Design / compliance (tiered - distinct from the generic design-services flag) ---
  const designComplianceTier = rawString(input, "exterior_design_compliance_scope") ?? "none";
  const designComplianceItemId = DESIGN_COMPLIANCE_ITEM_BY_TIER[designComplianceTier];
  if (designComplianceItemId) {
    const designCompliance = applyItem(catalog, designComplianceItemId, { finishLevel, regionalMultiplier });
    if (designCompliance) applied.push(designCompliance);
    if (designComplianceTier === "heritage_hoa_compliance") {
      warnings.push({
        code: "exterior_heritage_hoa_review_timeline",
        message:
          "Heritage-district or HOA/condo-board approval processes can add significant time (weeks to months) and their own application/reporting fees beyond this allowance, and may restrict material/colour choices in ways that change the cost of the categories selected above. Confirm requirements and timeline with the relevant authority before finalizing budget.",
        severity: "caution"
      });
    }
  } else {
    exclusions.push({ code: "excludes_exterior_design_compliance", message: "No architectural/design services or heritage/HOA compliance review was requested - excluded from this estimate." });
  }

  // --- Electrical (generic flag - e.g. exterior lighting/outlet work) -----------
  if (input.workType.electricalChanges === "yes" && findItem(catalog, "exterior_electrical")) {
    const electrical = applyItem(catalog, "exterior_electrical", { finishLevel, regionalMultiplier });
    if (electrical) applied.push(electrical);
  } else {
    exclusions.push({ code: "excludes_exterior_electrical", message: "No exterior electrical work (e.g. lighting, outlets) was requested - excluded from this estimate." });
  }

  // --- Demolition (percentage of whatever scope was actually selected above) ---
  // Exterior has no single anchor line item to base a percentage on (see the
  // module doc comment) - demolition is instead priced as a percentage of
  // the subtotal of every category actually selected so far, which is the
  // closest honest equivalent to "percentage of the base renovation cost"
  // every other project type uses.
  if (input.workType.demolitionRequired === "yes" && findItem(catalog, "exterior_demolition") && applied.length > 0) {
    const subtotal = sumTypical(applied);
    const demolition = applyItem(catalog, "exterior_demolition", { regionalMultiplier, baseAmountForPercentage: subtotal });
    if (demolition) applied.push(demolition);
  }

  // --- Accessibility -------------------------------------------------------------
  if (input.accessibility.required === "yes") {
    const accessibility = applyItem(catalog, "exterior_accessibility", { finishLevel, regionalMultiplier });
    if (accessibility) applied.push(accessibility);
  }

  // --- Special handling: standing, exterior-specific disclosures ----------------
  warnings.push({
    code: "exterior_weather_seasonal_access_unknown",
    message:
      "Exterior work is weather- and season-dependent in most of Canada, and site access (scaffolding, staging materials, property line clearances) can't be assessed from a questionnaire. This estimate assumes normal seasonal access with no unusual site constraints.",
    severity: "caution"
  });

  const anchorMissing = sizeDependentCategoryRequestedWithoutSize;

  const output: ProjectCalculationOutput = { applied, assumptions, exclusions, anchorMissing, warnings };
  return output;
};
